import React from 'react';

/**
 * Ask AI's answers, formatted: paragraphs, "- " and "1. " lists, **bold**,
 * `code`, a short "Label:" line in bold, and the `{NAME}` / `#1234` pills
 * the dock has always drawn. A deliberately small Markdown subset, turned
 * into React elements — never HTML — so nothing in a model's answer can
 * inject markup.
 */

const Inline: React.FC<{ text: string }> = ({ text }) => {
  const parts = text.split(
    /(\*\*[^*]+\*\*|`[^`]+`|\{[A-Z][A-Z0-9_]*\}|#-?\d{1,6}\b)/g
  );
  return (
    <>
      {parts.map((part, i) => {
        if (/^\*\*[^*]+\*\*$/.test(part)) {
          // eslint-disable-next-line react/no-array-index-key
          return <strong key={i}>{part.slice(2, -2)}</strong>;
        }
        if (/^`[^`]+`$/.test(part)) {
          return (
            // eslint-disable-next-line react/no-array-index-key
            <code key={i} className="c-rich-answer__code">
              {part.slice(1, -1)}
            </code>
          );
        }
        if (/^\{[A-Z][A-Z0-9_]*\}$/.test(part)) {
          return (
            // eslint-disable-next-line react/no-array-index-key
            <span key={i} className="c-ai-assist__ref c-ai-assist__ref--var">
              {part.slice(1, -1)}
            </span>
          );
        }
        if (/^#-?\d{1,6}$/.test(part)) {
          return (
            // eslint-disable-next-line react/no-array-index-key
            <span key={i} className="c-ai-assist__ref c-ai-assist__ref--node">
              {part.slice(1)}
            </span>
          );
        }
        // eslint-disable-next-line react/no-array-index-key
        return <React.Fragment key={i}>{part}</React.Fragment>;
      })}
    </>
  );
};

type Block =
  | { kind: 'p'; lines: string[] }
  | { kind: 'ul' | 'ol'; items: string[] }
  | { kind: 'label'; text: string };

const BULLET = /^\s*[-*•]\s+(.*)$/;
const NUMBERED = /^\s*\d+[.)]\s+(.*)$/;
// "### Heading" or a short line that is only bold ("**Why:**").
const HEADING = /^\s*#{1,4}\s+(.*)$/;
const BOLD_LINE = /^\s*\*\*([^*]{1,60})\*\*:?\s*$/;

export const parseAnswer = (text: string): Block[] => {
  const blocks: Block[] = [];
  const push = (b: Block) => blocks.push(b);
  text.split(/\r?\n/).forEach((raw) => {
    const line = raw.trimEnd();
    const last = blocks[blocks.length - 1];
    if (!line.trim()) {
      // A blank line ends a paragraph or list.
      if (last) push({ kind: 'p', lines: [] });
      return;
    }
    let m: RegExpMatchArray | null;
    if ((m = line.match(BULLET)) || (m = line.match(NUMBERED))) {
      const kind = BULLET.test(line) ? 'ul' : 'ol';
      if (last && last.kind === kind) last.items.push(m[1]);
      else push({ kind, items: [m[1]] });
      return;
    }
    if ((m = line.match(HEADING)) || (m = line.match(BOLD_LINE))) {
      push({
        kind: 'label',
        text: m[1].replace(/\*\*/g, '').replace(/:\s*$/, ''),
      });
      return;
    }
    if (last && last.kind === 'p') last.lines.push(line.trim());
    else push({ kind: 'p', lines: [line.trim()] });
  });
  return blocks.filter((b) => b.kind !== 'p' || b.lines.length > 0);
};

export const RichAnswer: React.FC<{ text: string }> = ({ text }) => (
  <div className="c-rich-answer">
    {parseAnswer(text).map((block, i) => {
      if (block.kind === 'label') {
        return (
          // eslint-disable-next-line react/no-array-index-key
          <p key={i} className="c-rich-answer__label">
            <Inline text={block.text} />
          </p>
        );
      }
      if (block.kind === 'p') {
        return (
          // eslint-disable-next-line react/no-array-index-key
          <p key={i}>
            <Inline text={block.lines.join(' ')} />
          </p>
        );
      }
      const List = block.kind;
      return (
        // eslint-disable-next-line react/no-array-index-key
        <List key={i}>
          {block.items.map((item, j) => (
            // eslint-disable-next-line react/no-array-index-key
            <li key={j}>
              <Inline text={item} />
            </li>
          ))}
        </List>
      );
    })}
  </div>
);
